"use client";

import { useEffect, useMemo, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

const formatCurrency = (value) =>
  parseFloat(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function TokenReserveWithdrawModal({
  isOpen,
  onClose,
  onSubmit,
  reserveBalance = 0,
  submitting = false,
}) {
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setAmount("");
      setMessage("");
    }
  }, [isOpen]);

  const numericAmount = parseFloat(amount) || 0;
  const balanceAfter = useMemo(
    () => Math.max(0, reserveBalance - numericAmount),
    [reserveBalance, numericAmount]
  );

  const isValid =
    numericAmount > 0 &&
    numericAmount <= reserveBalance &&
    message.trim().length > 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid || submitting) return;
    onSubmit({ amount: numericAmount, message: message.trim() });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Withdraw from Token Reserve
            </h3>
            <p className="text-sm text-gray-500">
              Record a withdrawal and add a note for the log
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-gray-400 transition-colors hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 px-6 py-5">
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Current balance</span>
              <span className="font-semibold text-yellow-700">
                ${formatCurrency(reserveBalance)}
              </span>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <span className="text-gray-600">Withdrawal amount</span>
              <span className="font-semibold text-red-600">
                -${formatCurrency(numericAmount)}
              </span>
            </div>
            <div className="mt-2 border-t border-yellow-200 pt-2 flex items-center justify-between">
              <span className="font-medium text-gray-700">Balance after</span>
              <span className="text-lg font-bold text-gray-900">
                ${formatCurrency(balanceAfter)}
              </span>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700">
              Withdrawal amount
            </label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              disabled={submitting}
            />
            {numericAmount > reserveBalance && (
              <p className="mt-1 text-sm text-red-600">
                Amount exceeds available balance
              </p>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700">
              Message / note
            </label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={`e.g. The Total Toke Money from 04/27 - 05/10 was $5,282. We will use $3,392 and $1,890 will be placed in Toke Reserve.`}
              rows={4}
              disabled={submitting}
            />
            <p className="mt-1 text-xs text-gray-500">
              This message is saved in the withdrawal log for reference.
            </p>
          </div>

          <div className="flex justify-end gap-3 border-t pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!isValid || submitting}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                "Confirm Withdrawal"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
